import type { CostsToDate } from '../../../../api/initiative-client/initiative-client.type';
import type { ProjectsResponse } from '../../../../api/projects-client/projects-client.type';
import type { Epic } from '../../../../api/tasks-client/task-client.type';
import i18n from '../../../../base-dictionary';
import { graphChefchaouenBlue, secondaryBase } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import type { ExtendedComboboxItem } from '../../../../ui-library/select/select.type';
import { ChartDataType, CostMode, type ChartDataItem } from './cost-tab.type';

/**
 * Percentage threshold for epic/team visibility in the tree map.
 * Epics/teams with a percentage below this threshold will be grouped into the "Other" category.
 * This improves visualization clarity by preventing the tree map from becoming cluttered with tiny segments
 * that would be difficult to interact with and visually interpret.
 */
const THRESHOLD = 5;

/**
 * Minimum size (in percentage) for individual initiative cells in the tree map.
 * Initiatives with sizes between THRESHOLD and MIN_CELL_SIZE will be artificially
 * increased to this size to ensure they remain clickable and visible.
 */
const MIN_CELL_SIZE = 8;

/**
 * Calculates the percentage of a value relative to a total
 *
 * @param {number} value - The value to calculate percentage for
 * @param {number} total - The total value
 * @returns {number} The percentage rounded to 2 decimal places
 */
const getPercentage = (value: number, total: number): number => {
  if (total === 0) return 0;
  return Math.round((value / total) * 100 * 100) / 100;
};

/**
 * Generate tree map data for epics based on costs_to_date
 *
 * @param {CostsToDate} costs - The costs to date information containing epic cost data
 * @param {Epic[]} epics - Array of epics to match with cost data
 * @returns {ChartDataItem[] | []} Array of chart data items for the tree map visualization or empty array if no data
 */
const generateEpicsTreeMapData = (costs: CostsToDate, epics: Epic[]): ChartDataItem[] | [] => {
  if (!costs.epics || Object.keys(costs.epics).length === 0) {
    return [];
  }

  const totalCost = Object.values(costs.epics).reduce((acc, cost) => acc + cost, 0);

  const rawData = Object.entries(costs.epics)
    .map(([epicId, cost]) => {
      const epic = epics.find((e) => e.id === epicId);

      if (!epic) return null;

      const percentage = getPercentage(cost, totalCost);
      let displayPercentage = percentage;
      if (percentage >= THRESHOLD && percentage < MIN_CELL_SIZE) {
        displayPercentage = MIN_CELL_SIZE;
      }

      return {
        id: epicId,
        title: epic.title,
        name: epic.name,
        size: displayPercentage,
        actualSize: cost,
        percentage,
        fill: graphChefchaouenBlue,
        type: ChartDataType.Epic,
      };
    })
    .filter(Boolean) as ChartDataItem[];

  return calculateTreeMapData(rawData, 'epics');
};

/**
 * Generate tree map data for teams based on costs_to_date
 *
 * @param {CostsToDate} costs - The costs to date information containing team cost data
 * @param {ProjectsResponse[]} teams - Array of teams to match with cost data
 * @returns {ChartDataItem[] | []} Array of chart data items for the tree map visualization or empty array if no data
 */
const generateTeamsTreeMapData = (costs: CostsToDate, teams: ProjectsResponse[]): ChartDataItem[] | [] => {
  if (!costs.teams || Object.keys(costs.teams).length === 0) {
    return [];
  }

  const totalCost = Object.values(costs.teams).reduce((acc, cost) => acc + cost, 0);

  const rawData = Object.entries(costs.teams)
    .map(([teamId, cost]) => {
      const team = teams.find((t) => t.id === teamId);

      if (!team) return null;

      const percentage = getPercentage(cost, totalCost);
      let displayPercentage = percentage;
      if (displayPercentage >= THRESHOLD && displayPercentage < MIN_CELL_SIZE) {
        displayPercentage = MIN_CELL_SIZE;
      }

      return {
        id: teamId,
        title: team.name,
        size: displayPercentage,
        actualSize: cost,
        percentage: getPercentage(cost, totalCost),
        fill: graphChefchaouenBlue,
        type: ChartDataType.Team,
      };
    })
    .filter(Boolean) as ChartDataItem[];

  return calculateTreeMapData(rawData, 'teams');
};

/**
 * Calculates tree map data for visualization based on chart data items.
 *
 * This function processes chart data to create a tree map visualization structure.
 * It separates items into two categories:
 * 1. Visible items - those with percentage values above the defined threshold
 * 2. "Other" items - those with percentage values below the threshold
 *
 * The "Other" tile is guaranteed to have a minimum size for visibility and clickability,
 * even if the combined small items are very tiny.
 *
 * @param {ChartDataItem[]} rawData - Array of raw chart data items
 * @param {'epics' | 'teams'} dataType - Type of data being processed
 * @returns {ChartDataItem[]} Processed data array ready for tree map visualization
 */
const calculateTreeMapData = (rawData: ChartDataItem[], dataType: 'epics' | 'teams'): ChartDataItem[] => {
  const visibleItems = rawData.filter((item) => item.percentage >= THRESHOLD);
  const otherItems = rawData.filter((item) => item.percentage < THRESHOLD);

  if (otherItems.length === 0) {
    return visibleItems.sort((a, b) => b.size - a.size);
  }

  const otherCost = otherItems.reduce((acc, item) => acc + item.actualSize, 0);
  const otherPercentage = otherItems.reduce((acc, item) => acc + item.percentage, 0);

  const otherData: Record<string, { title: string; size: number; percentage: number }> = {};
  otherItems.forEach((item) => {
    otherData[item.id] = { title: item.title, size: item.size, percentage: item.percentage };
  });

  // Ensure minimum size for "Other" tile
  const displaySize = Math.max(otherPercentage, calculateMinimumSizeValue(rawData, otherPercentage));

  const otherItem: ChartDataItem = {
    id: 'other',
    title: i18n.t('strategy.initiatives.other'),
    size: displaySize,
    actualSize: otherCost,
    percentage: otherPercentage,
    fill: graphChefchaouenBlue,
    type: ChartDataType.Other,
    ...(dataType === 'epics' ? { otherEpicsData: otherData } : { otherTeamsData: otherData }),
  };

  return [...visibleItems.sort((a, b) => b.size - a.size), otherItem];
};

/**
 * Calculates the minimum size value for the "Other" tile based on the total data
 *
 * @param {ChartDataItem[]} rawData - Array of raw chart data items
 * @param {number} actualOtherSize - The actual combined size of other items
 * @returns {number} The minimum size value to ensure proper visibility
 */
const calculateMinimumSizeValue = (rawData: ChartDataItem[], actualOtherSize: number): number => {
  const totalSize = rawData.reduce((acc, item) => acc + item.size, 0);

  // If total size is zero, use fallback minimum to ensure visibility
  if (totalSize === 0) {
    return MIN_CELL_SIZE;
  }

  const minimumSize = (totalSize * MIN_CELL_SIZE) / 100;

  return Math.max(minimumSize, actualOtherSize);
};

/**
 * Returns a list of options for the cost mode dropdown
 * Each option contains a label for display and a value from the CostMode enum
 *
 * @returns {ExtendedComboboxItem[]} Array of options for the cost mode selection
 */
const getCostModeOptions = (): ExtendedComboboxItem[] => {
  return [
    {
      label: i18n.t('strategy.initiative_performance.labels.contributing_epics'),
      value: CostMode.ContributingEpics,
      leftSection: <Icon name="move_down" size={20} color={secondaryBase} />,
    },
    {
      label: i18n.t('strategy.initiative_performance.labels.contributing_teams'),
      value: CostMode.ContributingTeams,
      leftSection: <Icon name="people_alt" size={20} color={secondaryBase} />,
    },
  ];
};

export { generateEpicsTreeMapData, generateTeamsTreeMapData, getCostModeOptions };
